import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { patientsAPI } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { subjectLabel, subjectLabelSingular, isClinicalRole } from "@/lib/modules";
import { validate, patientSchema } from "@/lib/validations";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus,
  Search,
  User,
  Phone,
  Mail,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_PATIENT = {
  name: "", email: "", phone: "", date_of_birth: "", gender: "", kind: "patient",
  occupation: "", emergency_contact_name: "", emergency_contact_phone: "",
  medical_history: "", allergies: "", notes: "",
};

export default function PatientsPage() {
  const navigate = useNavigate();
  const { user, tenant } = useAuth();
  // Fase 5a: roles no clínicos no capturan campos médicos (el backend los rechaza).
  const clinical = isClinicalRole(user?.role);
  const label = subjectLabel(tenant?.modules);
  const labelS = subjectLabelSingular(tenant?.modules);
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY_PATIENT);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await patientsAPI.list({ search, limit: 50 });
      setPatients(res.data.patients || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Error cargando pacientes");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(fetchPatients, 300);
    return () => clearTimeout(timer);
  }, [fetchPatients]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const v = validate(patientSchema, form);
    if (!v.ok) {
      setErrors(v.errors);
      toast.error(Object.values(v.errors)[0]);
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      const res = await patientsAPI.create(form);
      toast.success("Paciente creado");
      setShowNew(false);
      setForm(EMPTY_PATIENT);
      fetchPatients();
      setTimeout(() => navigate(`/pacientes/${res.data.id}`), 150);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear");
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="patients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">{label}</h1>
          <p className="text-muted-foreground text-sm mt-0.5">{total} {label.toLowerCase()}</p>
        </div>
        <Button
          size="sm"
          className="rounded-full"
          onClick={() => { setForm(EMPTY_PATIENT); setShowNew(true); }}
          data-testid="new-patient-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          Agregar {labelS.toLowerCase()}
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nombre..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="patient-search"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-2">
          {[1,2,3,4].map(i => <div key={i} className="h-16 rounded-xl bg-muted animate-pulse" />)}
        </div>
      ) : patients.length > 0 ? (
        <div className="space-y-2">
          {patients.map((p) => (
            <Card
              key={p.id}
              className="cursor-pointer hover:border-primary/30 transition-colors"
              onClick={() => navigate(`/pacientes/${p.id}`)}
              data-testid={`patient-card-${p.id}`}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold flex-shrink-0">
                  {p.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{p.name}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    {p.phone && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Phone className="w-3 h-3" />{p.phone}
                      </span>
                    )}
                    {p.email && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Mail className="w-3 h-3" />{p.email}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-muted-foreground">
          <User className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">
            {search ? `No se encontraron ${label.toLowerCase()}` : `Aun no tienes ${label.toLowerCase()}`}
          </p>
          {!search && (
            <Button variant="link" size="sm" className="mt-1" onClick={() => setShowNew(true)}>
              Agregar {labelS.toLowerCase()}
            </Button>
          )}
        </div>
      )}

      {/* New Patient Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="new-patient-dialog">
          <DialogHeader>
            <DialogTitle>Agregar {labelS.toLowerCase()}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Nombre completo *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="Nombre del paciente"
                  required
                  data-testid="patient-name-input"
                />
                {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  placeholder="email@ejemplo.com"
                  data-testid="patient-email-input"
                />
              </div>
              <div>
                <Label>Telefono</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  placeholder="+52 ..."
                  data-testid="patient-phone-input"
                />
              </div>
              <div>
                <Label>Fecha de nacimiento</Label>
                <Input
                  type="date"
                  value={form.date_of_birth}
                  onChange={(e) => setField("date_of_birth", e.target.value)}
                  data-testid="patient-dob-input"
                />
              </div>
              <div>
                <Label>Genero</Label>
                <Select value={form.gender} onValueChange={(v) => setField("gender", v)}>
                  <SelectTrigger data-testid="patient-gender-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="masculino">Masculino</SelectItem>
                    <SelectItem value="femenino">Femenino</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={form.kind} onValueChange={(v) => setField("kind", v)}>
                  <SelectTrigger data-testid="patient-kind-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="patient">Paciente</SelectItem>
                    <SelectItem value="athlete">Atleta</SelectItem>
                    <SelectItem value="both">Ambos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label>Ocupacion</Label>
                <Input
                  value={form.occupation}
                  onChange={(e) => setField("occupation", e.target.value)}
                  placeholder="Ocupacion del paciente"
                  data-testid="patient-occupation-input"
                />
              </div>
            </div>
            <div className="border-t border-border pt-3">
              <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">Contacto de emergencia</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Nombre</Label>
                  <Input
                    value={form.emergency_contact_name}
                    onChange={(e) => setField("emergency_contact_name", e.target.value)}
                    data-testid="patient-emergency-name"
                  />
                </div>
                <div>
                  <Label>Telefono</Label>
                  <Input
                    value={form.emergency_contact_phone}
                    onChange={(e) => setField("emergency_contact_phone", e.target.value)}
                    data-testid="patient-emergency-phone"
                  />
                </div>
              </div>
            </div>
            {clinical && (
              <div>
                <Label>Antecedentes medicos</Label>
                <Textarea
                  value={form.medical_history}
                  onChange={(e) => setField("medical_history", e.target.value)}
                  rows={2}
                  placeholder="Historial medico relevante..."
                  data-testid="patient-history-input"
                />
              </div>
            )}
            {clinical && (
              <div>
                <Label>Alergias</Label>
                <Input
                  value={form.allergies}
                  onChange={(e) => setField("allergies", e.target.value)}
                  placeholder="Alergias conocidas"
                  data-testid="patient-allergies-input"
                />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowNew(false)}>Cancelar</Button>
              <Button type="submit" className="rounded-full" disabled={saving} data-testid="patient-save-btn">
                {saving ? "Guardando..." : "Crear paciente"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
